import { useTextEditorState, useUpdatePin } from '@lessonup/pin-renderer';
import { FillInTheBlanksAreaPinComponent, FontFamily, fontSizes } from '@lessonup/pins-shared';
import {
  BodyText,
  IconAlignHorizontalCenter,
  IconAlignHorizontalLeft,
  IconAlignHorizontalRight,
  IconFormatListBullet,
  IconFormatListNumbered,
  IconFormatStrikethrough,
  IconFormatSubscript,
  IconFormatSuperscript,
  IconFormatUnderlined,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import { isUndefined } from 'lodash';
import React, { useMemo } from 'react';
import {
  ContextSidebarCollapsibleGroup,
  ContextSidebarGroup,
} from '../../../../../../../components/ContextSidebar/ContextSidebarGroup';
import { useEffectiveActivePinComponentSettings } from '../../../../../context/EditorContext/hooks/useEditorState';
import { useEditorTranslation } from '../../../../../hooks/useEditorTranslation';
import {
  createFontFamilyOptions,
  createFontWeightStyleOptions,
  FontWeightStyle,
} from '../../../../../utils/pinComponentSettings/textPinComponent';
import { teacherTextEditorStore } from '../../../../../utils/yjs/EditorYjs/teacherTextEditorStore';
import { SettingsToggle } from '../../../../SettingsToggle/SettingsToggle';
import { NoFocusPicker, NoFocusPickerProps } from '../../CustomSelect/NoFocusPicker';
import { SteppedNumberInput } from '../../CustomSelect/SteppedNumberInput';
import { ColorPickerSetting } from '../../SharedSettings/ColorPickerSetting';
import { BackgroundColorSetting } from '../sharedComponentSettings/BackgroundColorSetting';
import { LineHeightSetting } from '../sharedComponentSettings/LineHeightSetting';
import { ParagraphSetting } from '../sharedComponentSettings/ParagraphSetting';
import { PinComponentSettingsProps } from '../types';

export const FillInTheBlanksAreaPinComponentSettings: React.FC<
  PinComponentSettingsProps<FillInTheBlanksAreaPinComponent>
> = ({ pinComponent }) => {
  const { t } = useEditorTranslation();
  const dispatch = useUpdatePin();

  const { textHandler: textEditorHandler } = useTextEditorState(teacherTextEditorStore);
  const fillInTheBlanksSettings = useEffectiveActivePinComponentSettings('FILL_IN_THE_BLANKS_AREA', {
    settings: pinComponent.settings,
  });

  const fontFamilyPicker: NoFocusPickerProps<FontFamily> = {
    value: fillInTheBlanksSettings.fontFamily as FontFamily,
    options: createFontFamilyOptions(),
    styleOption: (option) => ({ fontFamily: `"${option?.value}"` }),
    onChange: (value) => {
      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          fontFamily: value,
        },
      });
    },
  };

  const fontWeightStylePicker: NoFocusPickerProps<FontWeightStyle> = {
    value: (fillInTheBlanksSettings.fontWeight ?? 'regular') as FontWeightStyle,
    valueLabel: t(`contextSidebar.text.fontWeightStyle.${fillInTheBlanksSettings.fontWeight ?? 'regular'}`),
    options: createFontWeightStyleOptions(t),
    styleOption: (option) => ({
      fontFamily: fillInTheBlanksSettings.fontFamily as FontFamily,
      fontWeight: option?.value === 'bold' || option?.value === 'bold-italic' ? 'bold' : 'normal',
      fontStyle: option?.value === 'regular-italic' || option?.value === 'bold-italic' ? 'italic' : 'normal',
    }),
    onChange: (value) => {
      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          fontWeight: value,
        },
      });
    },
  };

  const fontSizeToInt = (fontSize: string) => parseInt(fontSize.replace('px', ''));
  const intToFontSize = (fontSize: number) => `${fontSize}px`;

  const createFontSizeOptions = () =>
    fontSizes.map((fontSize) => ({ label: fontSize, value: fontSizeToInt(fontSize) }));

  const lineHeightOptions = useMemo(() => {
    return ['1.5', '1.75', '2', '2.25', '2.5'].map((value) => ({ label: value, value: value }));
  }, []);

  return (
    <>
      <ContextSidebarGroup title="Fill in the blanks">
        <SpaceBetween direction="y" spacing={spacing.size12}>
          <NoFocusPicker {...fontFamilyPicker} />
          <SpaceBetween direction="x" spacing={spacing.size12}>
            <NoFocusPicker {...fontWeightStylePicker} />
            <SteppedNumberInput
              label="Font Size"
              value={fillInTheBlanksSettings.fontSize ? fontSizeToInt(fillInTheBlanksSettings.fontSize) : undefined}
              options={createFontSizeOptions()}
              valueValidation={(value) => value === '' || /^\d+$/.test(value)}
              onTextChange={(value) => {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: {
                    fontSize: intToFontSize(value),
                  },
                });
              }}
              onChange={(value) => {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: {
                    fontSize: intToFontSize(value),
                  },
                });
              }}
            />
          </SpaceBetween>
          <ColorPickerSetting
            selectedColor={fillInTheBlanksSettings.fontColor}
            text={t('contextSidebar.settings.textColor')}
            ariaLabel={t('contextSidebar.settings.textColor')}
            hideTransparentOption
            onChange={(color) => {
              dispatch({
                type: 'updatePinComponentSettings',
                pinComponentId: pinComponent.id,
                settings: {
                  fontColor: color,
                },
                debounce: 1000,
              });
            }}
          />
        </SpaceBetween>
      </ContextSidebarGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textExtraOptionsSettings"
        title={t('contextSidebar.text.styling')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <TextElement textStyle="bodyText">{t('contextSidebar.text.decoration')}</TextElement>
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.underline', {
                enabled: textEditorHandler.underline.get(),
              })}
              icon={<IconFormatUnderlined />}
              checked={textEditorHandler.underline.get()}
              onChange={textEditorHandler.underline.toggle}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.strikethrough', {
                enabled: textEditorHandler.strikethrough.get(),
              })}
              icon={<IconFormatStrikethrough />}
              checked={textEditorHandler.strikethrough.get()}
              onChange={textEditorHandler.strikethrough.toggle}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <BodyText>{t('contextSidebar.text.listStyle')}</BodyText>
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.unorderedList', {
                enabled: textEditorHandler.bulletList.get(),
              })}
              icon={<IconFormatListBullet />}
              checked={textEditorHandler.bulletList.get()}
              onChange={textEditorHandler.bulletList.toggle}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.orderedList', {
                enabled: textEditorHandler.orderedList.get(),
              })}
              icon={<IconFormatListNumbered />}
              checked={textEditorHandler.orderedList.get()}
              onChange={textEditorHandler.orderedList.toggle}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <BodyText>{t('contextSidebar.text.other')}</BodyText>
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.superscript', {
                enabled: textEditorHandler.superscript.get(),
              })}
              icon={<IconFormatSuperscript />}
              checked={textEditorHandler.superscript.get()}
              onChange={textEditorHandler.superscript.toggle}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.subscript', {
                enabled: textEditorHandler.subscript.get(),
              })}
              icon={<IconFormatSubscript />}
              checked={textEditorHandler.subscript.get()}
              onChange={textEditorHandler.subscript.toggle}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
        <BackgroundColorSetting pinComponent={pinComponent} text={t('contextSidebar.settings.backgroundColor')} />
      </ContextSidebarCollapsibleGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textAlignmentSettings"
        title={t('contextSidebar.text.textAlignment.title')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.left', {
                enabled: fillInTheBlanksSettings.horizontalAlignment === 'left',
              })}
              icon={<IconAlignHorizontalLeft />}
              checked={
                fillInTheBlanksSettings.horizontalAlignment === 'left' ||
                isUndefined(fillInTheBlanksSettings.horizontalAlignment)
              }
              onChange={() => {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: { horizontalAlignment: 'left' },
                });
              }}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.center', {
                enabled: fillInTheBlanksSettings.horizontalAlignment === 'center',
              })}
              icon={<IconAlignHorizontalCenter />}
              checked={fillInTheBlanksSettings.horizontalAlignment === 'center'}
              onChange={() => {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: { horizontalAlignment: 'center' },
                });
              }}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.right', {
                enabled: fillInTheBlanksSettings.horizontalAlignment === 'right',
              })}
              icon={<IconAlignHorizontalRight />}
              checked={fillInTheBlanksSettings.horizontalAlignment === 'right'}
              onChange={() => {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: { horizontalAlignment: 'right' },
                });
              }}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textSpacingSettings"
        title={t('contextSidebar.settings.spacing.title')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <LineHeightSetting
          pinComponent={pinComponent}
          options={lineHeightOptions}
          key={`${pinComponent.id}-text-line-height-setting`}
        />
        <ParagraphSetting pinComponent={pinComponent} key={`${pinComponent.id}-text-paragraph-setting`} />
      </ContextSidebarCollapsibleGroup>
    </>
  );
};

const StyledSettingWrapper = styled.div`
  display: inline-flex;
`;
